import { Children, cloneElement } from "react";

import { USER_ACCESS } from "@/constants";
import { useUser } from "@/hooks/useUser";
import { User } from "@/types/User";
import { cx } from "@/utils";

import styles from "./AccessControl.module.css";

// Permissions, example: if you have 0=staff you have access to everything above 0.
export const ACCESS_LEVELS = {
  STAFF: 0, // Full access
  ADMIN: 1, // Admin user
  USER: 2, // Regular user, access to everything but staff functionality
  READONLY: 3, // Definition pending
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ElementChild = React.ReactElement<any, React.JSXElementConstructor<any>>;

// Define types for props when `visible` is set to true
type AccessControlVisibleProps = {
  visible: true;
  children: ElementChild;
};

// Define types for props when `visible` is not specified or set to false
type AccessControlHiddenProps = {
  visible?: false;
  children: React.ReactNode;
};

// Define the main props type for AccessControl
type AccessControlProps = {
  className?: string;
  fallback?: React.ReactElement | null;
  level?: number;
} & (AccessControlHiddenProps | AccessControlVisibleProps);

/**
 * Lock down part of the code or a feature to only be usable/visible by users belonging to a certain category
 */
const AccessControl: React.FunctionComponent<AccessControlProps> = ({
  className = undefined,
  children,
  visible = false,
  fallback = null,
  level = ACCESS_LEVELS.USER,
}) => {
  const { data, isLoading } = useUser();
  const user_type = (data?.data as User)?.user_type || "";
  const is_staff = (data?.data as User)?.is_staff || false;

  if (visible) {
    if (Children.count(children) !== 1) {
      throw new Error(
        "AccessControl with 'visible=true' should have exactly 1 direct child."
      );
    }
  }

  let userLevel = ACCESS_LEVELS.READONLY; // Start out with lowest access level.

  if (is_staff) {
    userLevel = ACCESS_LEVELS.STAFF;
  } else if (user_type === USER_ACCESS.USER) {
    userLevel = ACCESS_LEVELS.USER;
  } else if (user_type === USER_ACCESS.ADMIN) {
    userLevel = ACCESS_LEVELS.ADMIN;
  }

  const granted = userLevel <= level;

  if (isLoading) {
    return null;
  }

  return granted
    ? children
    : visible
      ? cloneElement(children as ElementChild, {
          className: cx(
            styles.root,
            className,
            (children as ElementChild)?.props?.className
          ),
          tabIndex: "-1",
          onClick: (e: React.MouseEvent) => {
            // overwrite any onClick handlers that are passed
            e.stopPropagation();
            e.preventDefault();
          },
        })
      : fallback;
};
export default AccessControl;
